<template>
  <ListboxRoot
    v-model="selectedUsers"
    @update:modelValue="$emit('change', $event)"
    by="id"
    multiple
    highlightOnHover
  >
    <ListboxContent class="flex flex-col gap-1">
      <template
        v-if="
          filteredSelectedUsers.length > 0 || filteredUnselectedUsers.length > 0
        "
      >
        <ListboxGroup
          v-if="filteredSelectedUsers.length > 0"
          class="flex flex-col gap-1"
        >
          <User
            v-for="selectedUser in filteredSelectedUsers"
            :key="selectedUser.id"
            :user="selectedUser"
            @update:checked="removeUser(selectedUser.id)"
          />
          <Separator
            v-if="filteredUnselectedUsers.length > 0"
            class="self-center w-[54px] border border-primary"
          />
        </ListboxGroup>
        <ListboxGroup class="flex flex-col gap-1">
          <User
            v-for="unselectedUser in filteredUnselectedUsers"
            :key="unselectedUser.id"
            :user="unselectedUser"
            :checked="false"
            @update:checked="selectedUsers.push(unselectedUser)"
          />
        </ListboxGroup>
      </template>
      <div v-else class="flex flex-col items-center">
        <State
          type="Users"
          :title="
            $t('components.empty_states.users.title.with_query', { query })
          "
          :text="$t('components.empty_states.users.description')"
        />
      </div>
    </ListboxContent>
  </ListboxRoot>
</template>

<script setup>
import { computed, ref } from "vue";
import { isEqual, sortBy } from "lodash";
import {
  ListboxContent,
  ListboxGroup,
  ListboxRoot,
  Separator,
} from "radix-vue";
import { User } from "@/SharedComponents/Modals/Content/Users";
import { State } from "@/SharedComponents/State";
import { useUsers } from "@/Composables/useUsers";

const emit = defineEmits(["change", "submit"]);

const props = defineProps({
  initialState: { type: Array, default: () => [] },
  selectedUsers: { type: Array, required: false },
  buildingIds: { type: Array, required: false },
  query: { type: String, required: false },
});

const selectedUsers = ref(
  props.selectedUsers ? [...props.selectedUsers] : [...props.initialState]
);
const { isLoading, data } = useUsers(props.buildingIds);

const users = computed(() => {
  if (isLoading.value) {
    return [];
  }
  return data.value;
});

const unselectedUsers = computed(() => {
  const selectedUserIds = selectedUsers.value.map((user) => user.id);
  if (selectedUserIds.length === 0) {
    return users.value;
  }
  return users.value.filter((user) => !selectedUserIds.includes(user.id));
});

const filteredSelectedUsers = computed(() => {
  if (!props.query) {
    return selectedUsers.value;
  }
  return selectedUsers.value.filter((user) => {
    return user.fullname.toLowerCase().includes(props.query);
  });
});

const filteredUnselectedUsers = computed(() => {
  if (!props.query) {
    return unselectedUsers.value;
  }
  return unselectedUsers.value.filter((user) => {
    return user.fullname.toLowerCase().includes(props.query);
  });
});

function removeUser(userId) {
  selectedUsers.value = selectedUsers.value.filter(
    (user) => user.id !== userId
  );
}

const isDirty = computed(() => {
  return !isEqual(sortBy(selectedUsers.value), sortBy(props.initialState));
});

function reset() {
  selectedUsers.value = [...props.initialState];
}

function submit() {
  if (!isDirty.value) {
    return;
  }
  emit("submit", selectedUsers.value);
}

defineExpose({
  isDirty,
  reset,
  submit,
});
</script>
